// ─────────────────────────────────────────────────────────────────────────────
// agentLoop.ts — client for the PER-AGENT loop toggle.
//
// THE LOOP IS THE SELF-CHECK on core/loop_binding.py: the agent's answer is
// checked against its own served DOCTRINE and repaired where the check finds a
// miss. It costs model calls and seconds, so the owner arms it PER AGENT — for
// an agent running unattended, where time is not the constraint — and leaves it
// off for an agent being sat with, because then the owner is the check.
//
// ── THIS FILE CARRIES NO OPINION ABOUT WHAT THE LOOP WOULD DO ───────────────
// `capability` is READ, never derived here. The rule that decides it lives in
// two backend places at once — `tier_folders.in_turn_scope` (is this a declared
// agent) and the three-way branch at `loop_harness.run` (does T1 come back) —
// and a copy of it in TypeScript would be a second definition of the one thing
// the button exists to state truthfully. That is the fault that produced a
// filter disagreeing with the store it filtered; the UI reads one canonical
// answer and renders it.
//
// ── FAIL CLOSED TOWARD OFF, WITH NO COERCION ───────────────────────────────
// `core/loop_toggle.py` refuses "true", 1 and "yes" deliberately: a value we did
// not write is a value we do not trust to mean consent. This client holds the
// same line — `enabled` is true ONLY for boolean true. Missing key, unreadable
// body, unknown capability: OFF, and `capability: 'unknown'` so the surface can
// say it does not know rather than paint a default that looks like a choice.
//
// ── THE RESPONSE MUST BE ABOUT THE AGENT WE ASKED FOR ──────────────────────
// THE OWNER'S RULE IS "TURNING IT ON FOR BUCK MUST NOT TOUCH DELTA", and the way
// a UI breaks that rule is not by writing the wrong key — it is by PAINTING a
// reply about one agent onto another after the user has moved on. So every
// response is checked against the identity it was requested for and REFUSED on
// a mismatch. The component adds the in-flight guard; this is the wire-level
// half, and the two are independent on purpose.
// ─────────────────────────────────────────────────────────────────────────────

import { apiBase, authToken } from './apiBase';

/**
 * PROVISIONAL — THE ONE LINE THAT MOVES WHEN SPARTICUS LANDS THE STATE SHAPE.
 *
 * The per-agent state does not exist yet: `core/loop_toggle.py` today stores a
 * SINGLE GLOBAL key (`loop_reshape` in memory/rexy_settings.json) and there is
 * no route of any kind. Sparticus owns where the per-agent state lives. The
 * surface is built against the contract in
 * scratchpad/agent_loop_toggle/SPARTICUS_CONTRACT.md and every assumption about
 * the wire is confined to this constant and `normalizeAgentLoop` below.
 */
export const AGENT_LOOP_PATH = '/rexy/agent-loop';

/** What the loop would ACTUALLY do for this agent. Backend-computed. */
export type AgentLoopCapability =
  /** Declared agent WITH doctrine: draft -> check -> repair, up to maxRounds. */
  | 'full'
  /** Declared agent, NO doctrine: the loop does not run. The turn's own answer
   *  stands, unchecked, and no extra model call is made. */
  | 'draft_only'
  /** Not a declared agent: `in_turn_scope` refuses, so the loop cannot run. */
  | 'out_of_scope'
  /** We could not read the state. NOT a state the backend reports. */
  | 'unknown';

const CAPABILITIES: readonly AgentLoopCapability[] = [
  'full', 'draft_only', 'out_of_scope',
];

export interface AgentLoopState {
  domain: string;
  agent: string;
  /** The stored per-agent setting. Absent/!== true ⇒ false. */
  enabled: boolean;
  /** Whether a setting exists AT ALL. False ⇒ nobody has ruled on this agent,
   *  which is NOT the same as somebody ruling it off. */
  hasSetting: boolean;
  capability: AgentLoopCapability;
  /** How much doctrine backs the check. 0 for every non-`full` capability. */
  doctrineRecords: number;
  /** Rounds the loop may take (loop_binding.MAX_ROUNDS). 0 when it cannot run. */
  maxRounds: number;
}

// apiBase and authToken are SHARED (src/api/apiBase.ts). This file used to
// carry its own copy of both, as five sibling clients did — six identical
// hardcodings of a host, and a built bundle pointed every one of them at
// herexy.com wherever it was served from. An instance fix leaves twins, so the
// answer moved to one module rather than being corrected here.


function asCount(v: any): number {
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0;
}

/**
 * The wire body → the state the surface renders. NEVER throws.
 *
 * `requested` is the identity the caller asked about. It is not decoration: a
 * body describing another agent is DISCARDED down to an unknown-capability OFF
 * for the requested agent, because rendering it would be the exact cross-agent
 * bleed the per-agent toggle exists to prevent.
 */
export function normalizeAgentLoop(
  d: any,
  requested: { domain: string; agent: string },
): AgentLoopState {
  const base: AgentLoopState = {
    domain: requested.domain,
    agent: requested.agent,
    enabled: false,
    hasSetting: false,
    capability: 'unknown',
    doctrineRecords: 0,
    maxRounds: 0,
  };
  if (!d || typeof d !== 'object') return base;

  // Identity gate. Compared case-insensitively because the backend keys agents
  // lowercase (`tier_folders.agent_key`) and domains uppercase, and a casing
  // difference is not a different agent. A MISSING field is not a match — an
  // unlabelled body could be about anyone.
  const sameAgent = typeof d.agent === 'string'
    && d.agent.trim().toLowerCase() === requested.agent.trim().toLowerCase();
  const sameDomain = typeof d.domain === 'string'
    && d.domain.trim().toUpperCase() === requested.domain.trim().toUpperCase();
  if (!sameAgent || !sameDomain) return base;

  // NO COERCION — mirrors core/loop_toggle.py. "true"/1/"yes" are all OFF.
  const enabled = d.enabled === true;
  const capability = CAPABILITIES.indexOf(d.capability) >= 0
    ? (d.capability as AgentLoopCapability)
    : 'unknown';

  return {
    domain: requested.domain,
    agent: requested.agent,
    // A capability we could not read cannot be reported as armed: we do not
    // know what it would arm. Off is the direction the whole mechanism fails in.
    enabled: capability === 'unknown' ? false : enabled,
    hasSetting: d.has_setting === true,
    capability,
    doctrineRecords: capability === 'full' ? asCount(d.doctrine_records) : 0,
    maxRounds: capability === 'full' ? asCount(d.max_rounds) : 0,
  };
}

async function loopFetch(
  requested: { domain: string; agent: string },
  init: RequestInit,
  query = '',
): Promise<AgentLoopState> {
  const res = await fetch(`${apiBase()}${AGENT_LOOP_PATH}${query}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken()}`,
      ...(init.headers || {}),
    },
  });
  if (!res.ok) throw new Error(`agent_loop_http_${res.status}`);
  return normalizeAgentLoop(await res.json(), requested);
}

/** GET the live per-agent loop state. */
export function getAgentLoop(domain: string, agent: string): Promise<AgentLoopState> {
  const q = `?domain=${encodeURIComponent(domain)}&agent=${encodeURIComponent(agent)}`;
  return loopFetch({ domain, agent }, { method: 'GET' }, q);
}

/** POST the per-agent setting. Returns the EFFECTIVE state the backend now holds
 *  — never the value that was requested. */
export function setAgentLoop(
  domain: string, agent: string, enabled: boolean,
): Promise<AgentLoopState> {
  return loopFetch({ domain, agent }, {
    method: 'POST',
    body: JSON.stringify({ domain, agent, enabled }),
  });
}
